import type { SelectProps } from "antd";
import { Select as AntdSelect, Button } from "antd";
import { ReactElement, useEffect, useMemo, useState } from "react";
import styles from "./styles.module.scss";

/**
 * Shared Select wrapper.
 *
 * Default: behaves like AntD Select.
 *
 * Extra capability (optional):
 * - withFooterActions (multiple/tags only): shows footer with Clear + Select.
 *   - Maintains a draft state while dropdown is open.
 *   - Apply commits draft via onChange; Clear resets draft.
 *   - Props: withFooterActions?, applyText?, clearText?
 *
 * Usage:
 * <Select mode="multiple" withFooterActions options={...} onChange={...} />
 * <Select mode="multiple" withFooterActions={false} ... /> // plain Select
 */
export { SelectButton } from "./SelectButton";
export type { SelectProps };

type FooterSelectProps = SelectProps & {
  /**
   * When true (and mode is multiple/tags), shows footer actions with draft state.
   */
  withFooterActions?: boolean;
  applyText?: string;
  clearText?: string;
};

export function Select(props: FooterSelectProps) {
  const {
    withFooterActions = false,
    applyText = "Select",
    clearText = "Clear Selection",
    dropdownRender,
    onDropdownVisibleChange,
    onChange,
    value,
    mode,
    options,
    ...rest
  } = props;

  const isMulti = mode === "multiple" || mode === "tags";
  const enableFooter = withFooterActions && isMulti;

  const [open, setOpen] = useState<boolean | undefined>(undefined);
  const [draft, setDraft] = useState<any[]>(
    Array.isArray(value) ? (value as any[]) : []
  );

  // Sync draft when value changes or when opening
  useEffect(() => {
    if (!enableFooter) return;
    if (open) {
      setDraft(Array.isArray(value) ? (value as any[]) : []);
    }
  }, [enableFooter, open, value]);

  const optionsMap = useMemo(() => {
    const map = new Map<any, any>();
    (options || []).forEach((opt) => {
      if ("value" in opt) {
        map.set(opt.value, opt);
      }
    });
    return map;
  }, [options]);

  const handleOpenChange = (next: boolean) => {
    if (enableFooter) {
      setOpen(next);
      if (next) {
        setDraft(Array.isArray(value) ? (value as any[]) : []);
      }
    }
    onDropdownVisibleChange?.(next);
  };

  const handleInternalChange: SelectProps["onChange"] = (vals, option) => {
    if (enableFooter) {
      setDraft(vals as any[]);
    } else {
      onChange?.(vals, option);
    }
  };

  const handleApply = () => {
    const optionList = draft.map((v) => optionsMap.get(v)).filter(Boolean);
    onChange?.(draft as any, optionList as any);
    setOpen(false);
  };

  const handleClear = () => {
    setDraft([]);
    onChange?.([], []);
    setOpen(false);
  };

  const renderDropdown = (menu: ReactElement) => {
    if (!enableFooter) {
      return dropdownRender ? dropdownRender(menu) : menu;
    }

    return (
      <div className={styles.dropdownPanel}>
        <div className={styles.dropdownBody}>{menu}</div>
        <div className={styles.dropdownFooter}>
          <Button
            type="text"
            onClick={handleClear}
            className={styles.textButton}
          >
            {clearText}
          </Button>
          <Button
            type="primary"
            onClick={handleApply}
            className={styles.primaryButton}
          >
            {applyText}
          </Button>
        </div>
      </div>
    );
  };

  const mergedClassName = [
    enableFooter ? styles.selectWithFooter : styles.selectBase,
    rest.className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <AntdSelect
      {...rest}
      mode={mode}
      options={options}
      value={enableFooter ? (open ? draft : value) : value}
      onChange={handleInternalChange}
      dropdownRender={renderDropdown}
      onDropdownVisibleChange={handleOpenChange}
      open={enableFooter ? open : rest.open}
      className={mergedClassName}
    />
  );
}
